import {
    action,
    computed,
    makeObservable,
    observable,
    reaction,
    runInAction,
} from "mobx";
import { AsyncTaskStatus, BaseStore } from "stores/BaseStore";
import {
    resolveRenderedLayout,
    resolveSizeConstraints,
    resolveSizeConstraintsById,
} from "./heights";
import type { HeightInputs, SizeConstraints } from "./heights";
import { moveItem, resizeItem } from "./keyboard";
import {
    LAYOUT_SETTLE_QUIET_MS,
    LAYOUT_SETTLE_TIMEOUT_MS,
    MAXIMUM_SAVED_CARD_HEIGHT,
} from "./layoutConfig";
import type { MoveDirection, ResizeDirection } from "./keyboard";
import type {
    LayoutMerge,
    LayoutPersistence,
    LayoutSnapshot,
} from "./persistence";
import { reconcileLayout } from "./reconcile";
import { createDefaultLayout } from "./seed";
import type {
    BlockReason,
    LayoutItem,
    LayoutObject,
    SignalsDashboardLayoutV1,
} from "./types";

type DashboardLayoutStoreParams = {
    dashboardId: string;
    objects: readonly LayoutObject[];
    /** The layout the dashboard was read with, or null to seed one from the cards. */
    initial: LayoutSnapshot | null;
    persistence: LayoutPersistence;
    /**
     * Called when a save had to add or drop cards that someone else added or
     * deleted after this page loaded, so the dashboard can load or remove them.
     */
    onMerged?: (merge: LayoutMerge) => void;
    /**
     * Called with every card's order after a save, as the server derived it
     * from the saved layout. Called before `onMerged`.
     */
    onCardOrders?: (orders: Record<string, number>) => void;
};

const KEYBOARD_RESIZE_DIRECTIONS: Record<MoveDirection, ResizeDirection> = {
    left: "narrower",
    right: "wider",
    up: "shorter",
    down: "taller",
};

const BLOCKED_ANNOUNCEMENTS: Record<BlockReason, string> = {
    "minimum-size": "The card is already at its minimum size.",
    "maximum-size": "The card is already at its maximum size.",
    boundary: "The card cannot move or grow beyond the grid boundary.",
    "vertical-compaction":
        "The card cannot move into an empty row because the layout compacts upward.",
    collision: "The requested layout change is not available.",
    "not-found": "The requested layout change is not available.",
};

const cloneLayout = (
    layout: SignalsDashboardLayoutV1,
): SignalsDashboardLayoutV1 => ({
    ...layout,
    items: layout.items.map((item) => ({ ...item })),
});

const getLayoutSignature = (layout: SignalsDashboardLayoutV1): string =>
    JSON.stringify(
        layout.items
            .map(({ objectId, x, y, width, height }) => ({
                objectId,
                x,
                y,
                width,
                height,
            }))
            .sort((left, right) => left.objectId.localeCompare(right.objectId)),
    );

const setsEqual = (left: ReadonlySet<string>, right: ReadonlySet<string>) =>
    left.size === right.size && [...left].every((value) => right.has(value));

/**
 * A measurement with its fraction dropped. Text renders at fractional heights,
 * and a fraction of a pixel over a row is not worth a whole extra row.
 */
const wholePixels = (heightPx: number) => Math.floor(heightPx);

/** Cards stored without the auto-height flag were sized by a user. */
const autoHeightIdsFrom = (
    snapshot: LayoutSnapshot,
    objects: readonly LayoutObject[],
): Set<string> => {
    const sizedIds = new Set(
        snapshot.layout.items
            .filter((item) => item.autoHeight !== true)
            .map((item) => item.objectId),
    );
    return new Set(
        objects.map((object) => object.id).filter((id) => !sizedIds.has(id)),
    );
};

const SAVE_LAYOUT_TASK = "Save Layout";

/**
 * Owns a dashboard's layout: the committed layout, the draft being edited, and
 * the rendered layout once measured card heights apply.
 *
 * Measurements arrive as plain numbers and the store never touches the DOM, so
 * the whole pipeline runs in tests without a browser.
 */
export default class DashboardLayoutStore extends BaseStore {
    private readonly dashboardId: string;
    private readonly persistence: LayoutPersistence;
    private readonly onMerged?: (merge: LayoutMerge) => void;
    private readonly onCardOrders?: (orders: Record<string, number>) => void;

    @observable.ref private objects: LayoutObject[];
    @observable.ref private committedLayout: SignalsDashboardLayoutV1;
    @observable.ref private draftLayout: SignalsDashboardLayoutV1 | null = null;
    @observable.ref private measuredHeights: Map<string, number> = new Map();
    /** Chart chrome: everything in a chart card that is not the plot. */
    @observable.ref private measuredChrome: Map<string, number> = new Map();
    /**
     * Cards nobody has resized, which therefore fit their content. A card
     * leaves this set when the user resizes its height and never rejoins.
     */
    @observable.ref private autoHeightIds: Set<string> = new Set();
    /** Restored on cancel, and compared against to tell whether a draft is dirty. */
    private autoHeightIdsBeforeEditing: Set<string> | null = null;
    private savesCommitted = 0;
    /**
     * Measurements captured when presentation mode starts. Presentation mode
     * narrows the container, which rewraps text and chart chrome; freezing
     * keeps a prepared dashboard exactly as prepared.
     */
    @observable.ref private frozenHeights: Map<string, number> | null = null;
    @observable.ref private frozenChrome: Map<string, number> | null = null;
    /**
     * Height inputs held still for the length of a drag or resize.
     *
     * The renderer re-syncs from the layout prop during a resize. If cards
     * re-measured mid-resize and changed the rendered layout, that layout
     * would reach the renderer while the pointer is down and fight it.
     */
    @observable.ref private gestureHeightInputs: HeightInputs | null = null;

    /**
     * Whether a drag or resize is in progress. A plain field, not an
     * observable: anything that read it during render would re-render when a
     * gesture starts, and a grid render mid-gesture makes the renderer
     * re-sync and drop the drag. Read it from event handlers only.
     */
    private gestureActive = false;
    isGestureActive = () => this.gestureActive;

    /**
     * Listeners called when a gesture starts or ends. Plain callback sets for
     * the same reason `gestureActive` is a plain field.
     */
    private readonly gestureStartListeners = new Set<() => void>();
    private readonly gestureEndListeners = new Set<() => void>();

    subscribeGestureStart = (listener: () => void): (() => void) => {
        this.gestureStartListeners.add(listener);
        return () => {
            this.gestureStartListeners.delete(listener);
        };
    };

    subscribeGestureEnd = (listener: () => void): (() => void) => {
        this.gestureEndListeners.add(listener);
        return () => {
            this.gestureEndListeners.delete(listener);
        };
    };

    /** Listeners told which cards were added after the store was built. */
    private readonly cardsAddedListeners = new Set<
        (objectIds: string[]) => void
    >();

    subscribeCardsAdded = (
        listener: (objectIds: string[]) => void,
    ): (() => void) => {
        this.cardsAddedListeners.add(listener);
        return () => {
            this.cardsAddedListeners.delete(listener);
        };
    };

    @observable isEditing = false;
    @observable announcement = "";

    constructor({
        dashboardId,
        objects,
        initial,
        persistence,
        onMerged,
        onCardOrders,
    }: DashboardLayoutStoreParams) {
        super("Dashboard Layout Store");
        makeObservable(this);
        this.dashboardId = dashboardId;
        this.persistence = persistence;
        this.onMerged = onMerged;
        this.onCardOrders = onCardOrders;
        this.objects = objects.map((object) => ({ ...object }));
        if (initial) {
            this.committedLayout = reconcileLayout(initial.layout, this.objects);
            this.autoHeightIds = autoHeightIdsFrom(initial, this.objects);
        } else {
            this.committedLayout = createDefaultLayout(this.objects);
            this.autoHeightIds = new Set(this.objects.map((object) => object.id));
        }
    }

    @computed
    get isSaving(): boolean {
        return this.getTaskLoading(SAVE_LAYOUT_TASK);
    }

    /** The committed or draft layout, before measured heights apply. */
    @computed
    get layout(): SignalsDashboardLayoutV1 {
        return this.draftLayout ?? this.committedLayout;
    }

    /** What renders: the layout with measured heights applied and compacted. */
    @computed
    get renderedLayout(): SignalsDashboardLayoutV1 {
        return resolveRenderedLayout(
            this.layout,
            this.objects,
            this.heightInputs,
        );
    }

    /** A card's width in grid columns, from the rendered layout. */
    getItemWidth = (objectId: string): number | undefined =>
        this.renderedLayout.items.find((item) => item.objectId === objectId)
            ?.width;

    /** Per-card size limits for the renderer. */
    @computed
    get sizeConstraints(): Map<string, SizeConstraints> {
        return resolveSizeConstraintsById(this.objects, this.heightInputs);
    }

    /**
     * One card's size limits from the current measurements, ignoring the
     * gesture freeze. The renderer only reads per-item limits when a gesture
     * starts, so a resize that narrows a card until its content wraps reads
     * the rising floor from here. Called from pointer handlers, never during
     * render, so it does not subscribe anything to mid-gesture measurements.
     */
    liveSizeConstraints = (objectId: string): SizeConstraints | undefined => {
        const object = this.findObject(objectId);
        if (!object) return undefined;

        return resolveSizeConstraints(object, this.liveHeightInputs);
    };

    private get liveHeightInputs(): HeightInputs {
        return {
            content: this.frozenHeights ?? this.measuredHeights,
            chrome: this.frozenChrome ?? this.measuredChrome,
            autoHeightIds: this.autoHeightIds,
        };
    }

    @computed
    private get heightInputs(): HeightInputs {
        return this.gestureHeightInputs ?? this.liveHeightInputs;
    }

    private findObject(objectId: string): LayoutObject | undefined {
        return this.objects.find((object) => object.id === objectId);
    }

    @action
    beginGesture = () => {
        if (!this.isEditing || this.gestureHeightInputs) return;
        this.gestureHeightInputs = this.heightInputs;
        this.gestureActive = true;
        this.gestureStartListeners.forEach((listener) => listener());
    };

    @action
    endGesture = () => {
        const wasActive = this.gestureActive;
        this.gestureHeightInputs = null;
        this.gestureActive = false;

        // After the flags are down, so a listener that re-measures is not
        // suppressed by the gesture that just ended.
        if (wasActive) {
            this.gestureEndListeners.forEach((listener) => listener());
        }
    };

    @computed
    get isDirty(): boolean {
        if (!this.draftLayout) return false;

        return (
            getLayoutSignature(this.draftLayout) !==
                getLayoutSignature(this.committedLayout) ||
            (this.autoHeightIdsBeforeEditing !== null &&
                !setsEqual(this.autoHeightIds, this.autoHeightIdsBeforeEditing))
        );
    }

    /**
     * Resolves once the rendered layout has held still for `quietMs`: every
     * card has reported its height and none has moved since. Resolves false
     * if it is still changing after `timeoutMs`. An export waits on this so
     * it never captures a card mid-grow or mid-slide.
     */
    waitForSettledLayout = ({
        quietMs = LAYOUT_SETTLE_QUIET_MS,
        timeoutMs = LAYOUT_SETTLE_TIMEOUT_MS,
    }: { quietMs?: number; timeoutMs?: number } = {}): Promise<boolean> =>
        new Promise((resolve) => {
            let quietTimer = 0;
            const finish = (settled: boolean) => {
                window.clearTimeout(quietTimer);
                window.clearTimeout(timeoutTimer);
                stopWatching();
                resolve(settled);
            };
            const restartQuietPeriod = () => {
                window.clearTimeout(quietTimer);
                quietTimer = window.setTimeout(() => finish(true), quietMs);
            };

            const stopWatching = reaction(
                () => getLayoutSignature(this.renderedLayout),
                restartQuietPeriod,
            );
            const timeoutTimer = window.setTimeout(
                () => finish(false),
                timeoutMs,
            );
            restartQuietPeriod();
        });

    /**
     * How many saves this store has committed. A layout fetched before the
     * count last changed is older than the one on screen.
     */
    get committedSaveCount(): number {
        return this.savesCommitted;
    }

    /**
     * Commits a layout the server sent after the dashboard's cards changed.
     * A draft in progress is kept and brought into step with the cards, and
     * keeps the heights the user chose for the cards it already held. Cancel
     * goes back to the server's.
     */
    @action
    applyServerSnapshot = (snapshot: LayoutSnapshot) => {
        const draftIds = this.draftLayout
            ? new Set(this.draftLayout.items.map((item) => item.objectId))
            : null;
        const draftAutoHeightIds = this.autoHeightIds;

        this.commitSnapshot(snapshot);
        if (!this.draftLayout || !draftIds) return;

        this.draftLayout = reconcileLayout(this.draftLayout, this.objects);
        const serverAutoHeightIds = this.autoHeightIds;
        this.autoHeightIdsBeforeEditing = serverAutoHeightIds;
        this.autoHeightIds = new Set(
            this.objects
                .map((object) => object.id)
                .filter((id) =>
                    draftIds.has(id)
                        ? draftAutoHeightIds.has(id)
                        : serverAutoHeightIds.has(id),
                ),
        );
    };

    private commitSnapshot(snapshot: LayoutSnapshot) {
        this.committedLayout = reconcileLayout(snapshot.layout, this.objects);
        this.autoHeightIds = autoHeightIdsFrom(snapshot, this.objects);
    }

    /**
     * Records a content-driven card's natural height. Charts report chrome
     * instead, because a chart fills its box and its height would just echo
     * the box.
     */
    @action
    setMeasuredHeight = (objectId: string, heightPx: number) => {
        if (!Number.isFinite(heightPx) || heightPx <= 0) return;

        const whole = wholePixels(heightPx);
        if (this.measuredHeights.get(objectId) === whole) return;

        const next = new Map(this.measuredHeights);
        next.set(objectId, whole);
        this.measuredHeights = next;
    };

    /** Records the non-plot height of a chart card. */
    @action
    setMeasuredChromeHeight = (objectId: string, heightPx: number) => {
        if (!Number.isFinite(heightPx) || heightPx < 0) return;

        const whole = wholePixels(heightPx);
        if (this.measuredChrome.get(objectId) === whole) return;

        const next = new Map(this.measuredChrome);
        next.set(objectId, whole);
        this.measuredChrome = next;
    };

    @action
    freezeHeights = () => {
        this.frozenHeights = new Map(this.measuredHeights);
        this.frozenChrome = new Map(this.measuredChrome);
    };

    @action
    unfreezeHeights = () => {
        this.frozenHeights = null;
        this.frozenChrome = null;
    };

    @action
    startEditing = () => {
        if (this.isSaving || this.isEditing) return;

        this.draftLayout = cloneLayout(this.committedLayout);
        this.autoHeightIdsBeforeEditing = new Set(this.autoHeightIds);
        this.isEditing = true;
        this.announcement = "Layout editing started.";
    };

    @action
    cancelEditing = () => {
        if (!this.isEditing) return;

        this.endGesture();
        this.draftLayout = null;
        if (this.autoHeightIdsBeforeEditing) {
            this.autoHeightIds = this.autoHeightIdsBeforeEditing;
            this.autoHeightIdsBeforeEditing = null;
        }
        this.isEditing = false;
        this.announcement = "Layout changes canceled.";
    };

    /**
     * Saves the draft. Resolves false when persistence fails, leaving the
     * draft in place so it can be retried or canceled; resolves true when it
     * saved or there was nothing to save. When the server had to add or drop
     * cards someone else added or deleted, `onMerged` hears which.
     */
    async save(): Promise<boolean> {
        if (
            !this.isEditing ||
            !this.draftLayout ||
            !this.isDirty ||
            this.isSaving
        ) {
            return true;
        }

        const nextLayout = cloneLayout(this.draftLayout);
        nextLayout.items = nextLayout.items.map((item) => ({
            ...item,
            height: Math.min(item.height, MAXIMUM_SAVED_CARD_HEIGHT),
            autoHeight: this.autoHeightIds.has(item.objectId)
                ? true
                : undefined,
        }));
        const requestSnapshot: LayoutSnapshot = { layout: nextLayout };

        const result = await this.setupAsyncTask(SAVE_LAYOUT_TASK, async () => {
            const accepted = await this.persistence.save(
                this.dashboardId,
                requestSnapshot,
            );
            // Commit whatever persistence accepted, so any normalisation it
            // applied is what renders. Cards the server added are not among
            // this store's objects yet; they arrive once the dashboard loads
            // them.
            const acceptedLayout = reconcileLayout(
                accepted.layout,
                this.objects,
            );

            runInAction(() => {
                this.endGesture();
                this.committedLayout = acceptedLayout;
                this.draftLayout = null;
                this.autoHeightIdsBeforeEditing = null;
                this.isEditing = false;
                this.announcement = "Layout saved.";
                this.savesCommitted += 1;
                this.onCardOrders?.(accepted.orders);
                // In the same action, so cards removed by someone else leave
                // before the next render instead of flashing at the bottom.
                if (accepted.merge) this.onMerged?.(accepted.merge);
            });
        });

        if (result === AsyncTaskStatus.Error) {
            runInAction(() => {
                this.announcement = "The layout could not be saved.";
            });
            return false;
        }
        return true;
    }

    /**
     * Merges a layout reported by the renderer after a drag or resize into the
     * draft. A merge rather than a replace, because the report can omit a card
     * that is briefly absent from the render.
     */
    @action
    updateDraftItems = (items: readonly LayoutItem[]) => {
        if (!this.isEditing || !this.draftLayout || this.isSaving) return;

        const projectedItemsById = new Map(
            items.map((item) => [item.objectId, { ...item }]),
        );
        const draftObjectIds = new Set(
            this.draftLayout.items.map((item) => item.objectId),
        );
        const mergedItems = [
            ...this.draftLayout.items.map((item) => {
                const projected = projectedItemsById.get(item.objectId);
                if (!projected) return item;
                if (!this.autoHeightIds.has(item.objectId)) return projected;
                // An auto-height card takes position and width from the
                // gesture and keeps its own height.
                return { ...projected, height: item.height };
            }),
            ...items
                .filter((item) => !draftObjectIds.has(item.objectId))
                .map((item) => ({ ...item })),
        ];
        const nextLayout = reconcileLayout(
            { ...this.draftLayout, items: mergedItems },
            this.objects,
        );
        this.draftLayout = nextLayout;
    };

    /**
     * Hands a card's height to the user. Only a height resize does this: a
     * move reports heights too, and treating that as a choice would freeze
     * every card the first time anyone rearranged the dashboard.
     */
    @action
    markUserSized = (objectId: string) => {
        if (!this.autoHeightIds.has(objectId)) return;

        const next = new Set(this.autoHeightIds);
        next.delete(objectId);
        this.autoHeightIds = next;
    };

    @action
    reconcileObjects = (objects: readonly LayoutObject[]) => {
        const knownIds = new Set(this.objects.map((object) => object.id));
        const arrivedIds = objects
            .map((object) => object.id)
            .filter((id) => !knownIds.has(id));
        if (arrivedIds.length > 0) {
            const nextAuto = new Set(this.autoHeightIds);
            arrivedIds.forEach((id) => nextAuto.add(id));
            this.autoHeightIds = nextAuto;
        }

        this.objects = objects.map((object) => ({ ...object }));
        this.committedLayout = reconcileLayout(
            this.committedLayout,
            this.objects,
        );

        if (this.draftLayout) {
            this.draftLayout = reconcileLayout(this.draftLayout, this.objects);
        }

        if (arrivedIds.length > 0) {
            this.cardsAddedListeners.forEach((listener) =>
                listener(arrivedIds),
            );
        }
    };

    /**
     * Remembers where every card sits and returns a function that puts them
     * back. A delete removes its card before the server answers; if the server
     * refuses, restoring brings the card back in its place and at its height,
     * where reconciling it would treat it as a new card for the bottom.
     */
    rememberPlacement = (): (() => void) => {
        const { objects, committedLayout, autoHeightIds } = this;
        return action(() => {
            this.objects = objects;
            this.committedLayout = committedLayout;
            this.autoHeightIds = autoHeightIds;
        });
    };

    /**
     * Moves a card one cell, or resizes it one cell when `resize` is set.
     * Returns whether the layout changed, and announces the outcome.
     */
    @action
    applyKeyboardOperation = ({
        objectId,
        direction,
        resize,
    }: {
        objectId: string;
        direction: MoveDirection;
        resize: boolean;
    }): boolean => {
        if (!this.isEditing || !this.draftLayout || this.isSaving) return false;

        const resizesHeight =
            resize && (direction === "up" || direction === "down");
        const startLayout =
            resizesHeight && this.autoHeightIds.has(objectId)
                ? this.withRenderedHeight(this.draftLayout, objectId)
                : this.draftLayout;

        const constraints = this.liveSizeConstraints(objectId);
        const result =
            resize && constraints
                ? resizeItem(
                      startLayout,
                      objectId,
                      KEYBOARD_RESIZE_DIRECTIONS[direction],
                      constraints,
                  )
                : moveItem(startLayout, objectId, direction);

        if (!result.changed) {
            this.announcement =
                BLOCKED_ANNOUNCEMENTS[result.reason ?? "not-found"];
            return false;
        }

        if (resizesHeight) this.markUserSized(objectId);
        this.draftLayout = result.layout;
        // Announce where the card renders: an auto-height card above it can
        // render taller than stored and push it down.
        const changedItem = this.renderedLayout.items.find(
            (item) => item.objectId === objectId,
        );
        this.announcement = !changedItem
            ? "Card changed."
            : resize
            ? `Card resized to ${changedItem.width} columns by ${changedItem.height} rows.`
            : `Card moved to column ${changedItem.x + 1}, row ${
                  changedItem.y + 1
              }.`;
        return true;
    };

    /**
     * An auto-height card's stored height can differ from the height it
     * renders at, and a keyboard height resize has to start from what is on
     * screen.
     */
    private withRenderedHeight(
        layout: SignalsDashboardLayoutV1,
        objectId: string,
    ): SignalsDashboardLayoutV1 {
        const rendered = this.renderedLayout.items.find(
            (item) => item.objectId === objectId,
        );
        if (!rendered) return layout;

        return {
            ...layout,
            items: layout.items.map((item) =>
                item.objectId === objectId
                    ? { ...item, height: rendered.height }
                    : item,
            ),
        };
    }
}
